/**
 * Finance — Review and Approval.
 *
 * One stage since Round 2: the same person sets the amount, approves it and
 * records the payment, and the claim goes straight to Released. There is no
 * Finance Approval queue and no Treasury queue behind this any more.
 *
 * The stage works by ownership: a claim sits unclaimed until somebody takes
 * it, and only that person (or Main Access) may then act. That is what stops
 * two people releasing the same payment, so the UI makes the owned/unowned
 * split obvious rather than showing everyone a form.
 *
 * Approving is terminal. The dialog therefore asks for everything at once —
 * amount, reference, value date — and says plainly that there is no later step
 * to correct it in.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { fmtDate, money, PAYMENT_STATUSES, periodLabel, type PaymentStatus } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { useCurrentUser } from '@/auth/AuthContext';
import { StatusBadge } from '@/components/Badge';
import { ClaimViewer } from '@/components/ClaimViewer';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import {
  ActionDialog,
  AMOUNT_EPSILON,
  ErrorNote,
  parseAmount,
  QueuePanel,
  todayIso,
  toTs,
  useAction,
  useQueue,
} from '@/components/QueueKit';
import { LoadingBlock } from '@/components/Spinner';
import { useToast } from '@/components/Toast';
import { downloadPendingClaims } from '@/components/admin/sheets';
import type { FinanceReviewQueueRow, PendingExportRow } from '@/types/claims';

export function FinanceReviewPage(): ReactNode {
  const user = useCurrentUser();
  const { toast } = useToast();

  const load = useCallback(() => api.financeReview.queue<FinanceReviewQueueRow[]>(), []);
  const { rows, error, reload } = useQueue<FinanceReviewQueueRow>(load);

  const [viewing, setViewing] = useState<FinanceReviewQueueRow | null>(null);
  const [reviewing, setReviewing] = useState<FinanceReviewQueueRow | null>(null);
  const [returning, setReturning] = useState<FinanceReviewQueueRow | null>(null);
  const [amount, setAmount] = useState('');
  const [remark, setRemark] = useState('');
  const [reference, setReference] = useState('');
  const [releaseDate, setReleaseDate] = useState(todayIso());
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('Released');
  const [returnReason, setReturnReason] = useState('');
  const [exporting, setExporting] = useState(false);
  const action = useAction();

  /** Main Access may act on a claim it does not own; a reviewer may not. */
  const canAct = useCallback(
    (row: FinanceReviewQueueRow): boolean =>
      user.systemAccess === 'MAIN_ACCESS' || row.financeReviewerId === user.id,
    [user],
  );

  const setOriginals = useCallback(
    async (row: FinanceReviewQueueRow, received: boolean): Promise<void> => {
      const ok = await action.run(async () => {
        await api.financeReview.setOriginalsReceived<unknown>({ claimId: row.id, received });
      });
      if (!ok) {
        toast(action.error ?? 'Could not update the originals mark.', 'err');
        return;
      }
      toast(
        received
          ? `Original receipts for ${row.id} marked as received.`
          : `Originals mark removed from ${row.id}.`,
        'ok',
      );
      await reload();
    },
    [action, toast, reload],
  );

  const take = useCallback(
    async (row: FinanceReviewQueueRow): Promise<void> => {
      const ok = await action.run(async () => {
        await api.financeReview.take<unknown>({ claimId: row.id });
      });
      if (!ok) {
        toast(action.error ?? 'Could not take this claim.', 'err');
        return;
      }
      toast(`${row.id} is now yours to review.`, 'ok');
      await reload();
    },
    [action, toast, reload],
  );

  const openReview = useCallback(
    (row: FinanceReviewQueueRow): void => {
      setReviewing(row);
      // Default to the full claimed amount: approving in full is the common
      // case, and pre-filling it means a reduction is always a deliberate edit.
      setAmount(String(row.financeReviewedAmount ?? row.claimedAmount));
      setRemark(row.financeReviewRemark ?? '');
      // The reference is never pre-filled. It is the one field that must come
      // from outside this system, and a default would be a guess at a bank
      // record.
      setReference('');
      setReleaseDate(todayIso());
      setPaymentStatus('Released');
      action.setError(null);
    },
    [action],
  );

  const openReturn = useCallback(
    (row: FinanceReviewQueueRow): void => {
      setReturning(row);
      setReturnReason('');
      action.setError(null);
    },
    [action],
  );

  const parsed = parseAmount(amount);
  const reduced =
    reviewing != null && parsed != null && parsed < reviewing.claimedAmount - AMOUNT_EPSILON;
  const overClaimed =
    reviewing != null && parsed != null && parsed > reviewing.claimedAmount + AMOUNT_EPSILON;
  // Mirrors the server's rules so nobody is round-tripped to learn them.
  const blocked =
    parsed == null ||
    parsed < 0 ||
    overClaimed ||
    (reduced && remark.trim() === '') ||
    reference.trim() === '';

  const approveAndRelease = useCallback(async (): Promise<void> => {
    if (!reviewing || parsed == null) return;
    const ok = await action.run(async () => {
      await api.financeReview.approveAndRelease<unknown>({
        claimId: reviewing.id,
        amount: parsed,
        remark,
        reference: reference.trim(),
        paymentStatus,
        releaseDate,
      });
    });
    if (!ok) return;
    toast(`${reviewing.id} approved and released: ${money(parsed)} (${reference.trim()}).`, 'ok');
    setReviewing(null);
    setViewing(null);
    await reload();
  }, [reviewing, parsed, remark, reference, paymentStatus, releaseDate, action, toast, reload]);

  const returnClaim = useCallback(async (): Promise<void> => {
    if (!returning) return;
    const ok = await action.run(async () => {
      await api.financeReview.return<unknown>({
        claimId: returning.id,
        reason: returnReason.trim(),
      });
    });
    if (!ok) return;
    toast(`${returning.id} was returned to the requestor.`, 'ok');
    setReturning(null);
    setViewing(null);
    await reload();
  }, [returning, returnReason, action, toast, reload]);

  const exportPending = useCallback(async (): Promise<void> => {
    setExporting(true);
    try {
      const data = await api.reporting.pendingExport<PendingExportRow[]>({});
      await downloadPendingClaims(data);
      // Rows, not claims: the export is one line per receipt now, and saying
      // "12 claims" over a 47-line sheet invites a miscount.
      toast(`Exported ${data.length} receipt row(s).`, 'ok');
    } catch (err) {
      toast(errorMessage(err), 'err');
    } finally {
      setExporting(false);
    }
  }, [toast]);

  const columns: ReadonlyArray<Column<FinanceReviewQueueRow>> = useMemo(
    () => [
      { key: 'id', header: 'Request ID', cell: (r) => <b>{r.id}</b> },
      { key: 'requester', header: 'Requestor', cell: (r) => r.requesterName },
      { key: 'department', header: 'Department', cell: (r) => r.requesterDepartment },
      { key: 'period', header: 'Period', cell: (r) => periodLabel(r.period) },
      { key: 'lines', header: 'Receipts', align: 'right', cell: (r) => r.rowCount },
      { key: 'claimed', header: 'Claimed', align: 'right', cell: (r) => money(r.claimedAmount) },
      {
        key: 'allowance',
        header: 'Monthly allowance',
        align: 'right',
        // Advisory, never a block: the figure is shown with what the employee
        // has already claimed this period so the reviewer can see the whole
        // month, and an overrun is marked rather than refused.
        cell: (r) =>
          r.requesterAllowance == null ? (
            <span className="muted">Not set</span>
          ) : (
            <span className={r.periodClaimedTotal > r.requesterAllowance ? 'allowance-over' : undefined}>
              {money(r.requesterAllowance)}
              <span className="allowance-used">
                {money(r.periodClaimedTotal)} claimed this period
              </span>
            </span>
          ),
      },
      { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
      {
        key: 'originals',
        header: 'Originals',
        // Finance's tick-off (Round 2, item 7). It gates nothing — a claim can
        // be released with the paper still outstanding — so this is a record
        // and a way to stop the reminders, not a control.
        cell: (r) =>
          r.originalsReceivedAt != null ? (
            <button
              type="button"
              className="btn btn-good btn-sm"
              title={`Received ${fmtDate(toTs(r.originalsReceivedAt))} — click to undo`}
              onClick={() => void setOriginals(r, false)}
              disabled={action.busy}
            >
              ✓ Received
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void setOriginals(r, true)}
              disabled={action.busy}
            >
              Mark received
              {r.originalsRemindersSent > 0 ? ` · ${r.originalsRemindersSent} reminder(s)` : ''}
            </button>
          ),
      },
      {
        key: 'owner',
        header: 'Owner',
        cell: (r) =>
          r.financeReviewerId == null ? (
            <span className="muted">Unclaimed</span>
          ) : (
            <span className={r.financeReviewerId === user.id ? 'own-owner' : undefined}>
              {r.financeReviewerId === user.id ? 'You' : (r.financeReviewerName ?? 'Another reviewer')}
            </span>
          ),
      },
      { key: 'submitted', header: 'Submitted', cell: (r) => fmtDate(toTs(r.createdAt)) },
      {
        key: 'actions',
        header: '',
        cell: (r) => (
          <div className="row-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setViewing(r)}>
              View
            </button>
            {r.status === 'For Finance Review' ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => void take(r)}
                disabled={action.busy}
              >
                Take
              </button>
            ) : canAct(r) ? (
              <>
                <button type="button" className="btn btn-good btn-sm" onClick={() => openReview(r)}>
                  Approve &amp; release
                </button>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => openReturn(r)}>
                  Return
                </button>
              </>
            ) : null}
          </div>
        ),
      },
    ],
    [user.id, take, canAct, openReview, openReturn, setOriginals, action.busy],
  );

  if (rows === null && error === null) return <LoadingBlock label="Loading the review queue…" />;

  const unclaimed = (rows ?? []).filter((r) => r.status === 'For Finance Review').length;

  return (
    <>
      <ErrorNote message={error} />

      <QueuePanel
        title="Finance — Review &amp; Approval"
        count={rows?.length ?? 0}
        actions={
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void exportPending()}
            disabled={exporting}
          >
            {exporting ? 'Exporting…' : '⬇ Export receipts'}
          </button>
        }
      >
        <p className="hint">
          {unclaimed} claim(s) unclaimed. Take a claim to work it — once taken, only you or the
          Main Access holder can act on it. Approving a claim also releases it: there is no
          separate Finance Approval or Treasury step.
        </p>
        <DataTable
          rows={rows ?? []}
          columns={columns}
          rowKey={(r) => r.id}
          empty={<EmptyState icon="🔎" title="Nothing is waiting for Finance." />}
        />
      </QueuePanel>

      {viewing ? (
        <ClaimViewer
          claimId={viewing.id}
          requesterName={viewing.requesterName}
          onClose={() => setViewing(null)}
          // Only on a claim this reviewer holds. The server re-checks.
          canEditDescriptions={viewing.status === 'Finance Reviewing' && canAct(viewing)}
        />
      ) : null}

      {reviewing ? (
        <ActionDialog
          title={`Approve and release ${reviewing.id}`}
          confirmLabel="Approve & release"
          onConfirm={() => void approveAndRelease()}
          onCancel={() => setReviewing(null)}
          busy={action.busy}
          disabled={blocked}
          error={action.error}
        >
          <p className="dialog-lead">
            {reviewing.requesterName} claimed <b>{money(reviewing.claimedAmount)}</b> for{' '}
            {periodLabel(reviewing.period)}. Set the amount to pay and record the payment — this
            completes the claim.
          </p>

          {reviewing.requesterAllowance != null ? (
            <p className={reviewing.periodClaimedTotal > reviewing.requesterAllowance ? 'form-error' : 'form-notice'}>
              Monthly allowance {money(reviewing.requesterAllowance)} ·{' '}
              {money(reviewing.periodClaimedTotal)} claimed for {periodLabel(reviewing.period)}
              {reviewing.periodClaimedTotal > reviewing.requesterAllowance
                ? ' — over the allowance. This does not block the claim; reduce the amount here if it should be capped.'
                : '.'}
            </p>
          ) : null}

          <div className="field">
            <label htmlFor="fr-amount">Finance Reviewed Amount</label>
            <input
              id="fr-amount"
              type="number"
              step="0.01"
              min="0"
              max={reviewing.claimedAmount}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {overClaimed ? (
              <p className="field-error">
                This cannot exceed the claimed amount of {money(reviewing.claimedAmount)}.
              </p>
            ) : null}
          </div>

          <div className="field">
            <label htmlFor="fr-remark">
              Remark {reduced ? <span className="req">(required — you are reducing the amount)</span> : '(optional)'}
            </label>
            <textarea
              id="fr-remark"
              rows={3}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="e.g. Expense exceeded the applicable company limit for the period."
            />
            {reduced && remark.trim() === '' ? (
              <p className="field-error">
                Explain the reduction — the requestor sees this remark.
              </p>
            ) : null}
          </div>

          <div className="field">
            <label htmlFor="fr-reference">
              Payment Reference <span className="req">(required)</span>
            </label>
            <input
              id="fr-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. PAY-2026-000441"
            />
            {reference.trim() === '' ? (
              <p className="field-error">
                Record the bank or voucher reference so this payment can be traced.
              </p>
            ) : null}
          </div>

          <div className="field">
            <label htmlFor="fr-date">Release Date</label>
            {/* Not always today: a transfer may be valued on an earlier
                banking day. */}
            <input
              id="fr-date"
              type="date"
              value={releaseDate}
              max={todayIso()}
              onChange={(e) => setReleaseDate(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="fr-payment-status">Payment Status</label>
            <select
              id="fr-payment-status"
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
            >
              {PAYMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <p className="hint">
            This approves the claim <b>and</b> records the payment. It moves straight to Released —
            there is no later step to correct it in.
          </p>
        </ActionDialog>
      ) : null}

      {returning ? (
        <ActionDialog
          title={`Return ${returning.id} to the requestor`}
          confirmLabel="Return claim"
          onConfirm={() => void returnClaim()}
          onCancel={() => setReturning(null)}
          busy={action.busy}
          disabled={returnReason.trim() === ''}
          error={action.error}
        >
          <p className="dialog-lead">
            {returning.requesterName} will be asked to correct and resubmit. Say what is wrong —
            they see this reason verbatim.
          </p>
          <div className="field">
            <label htmlFor="fr-return-reason">
              Reason <span className="req">(required)</span>
            </label>
            <textarea
              id="fr-return-reason"
              rows={3}
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="e.g. The 12 July receipt is not legible — please re-upload it."
            />
          </div>
        </ActionDialog>
      ) : null}
    </>
  );
}
